import React, { useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { useDispatch } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { QuickSearchFormValidationSchema, ParishQuickSearchFormValues, ParishLocationQuickSearchResult, ParishNameQuickSearchResult, ParishFilters } from './parish-model';
import { useApiResponseError } from '../../hooks/useApiError';
import { FormWrapper } from '../form-wrapper';
import { FormField } from '../../../../TCSMApp/global/components/form-element/form-field';
import { ApiSmallResponse } from '../../models/api-model';
import { replaceAll } from '../../../../TCSMApp/global/helpers/string-helpers';
import { Wysiwyg } from '../wysiwyg';
import { GetParishTypeOptions, QuickSearchByLocation, QuickSearchByName } from '../../ducks/parish-api';
import { useEpiParishSettings, useEpiParishSettingsLoaded } from '../../hooks/useEpiParishSettings';
import { DelayExecute } from '../../helpers/delay';

interface Props {
    performSearch: (filters: ParishFilters) => void;
    selectParishById: (parishId: string | number) => void;
    searchFilters: ParishFilters;
    clearForm: () => void;
}

function ParishSearch(props: Props) {
    const { performSearch, selectParishById, searchFilters, clearForm } = props;
    const dispatch = useDispatch();

    const parishSettingsLoaded = useEpiParishSettingsLoaded();
    const parishSettings = useEpiParishSettings();
    const apiErrors = useApiResponseError();

    const initialLocationResults: ApiSmallResponse<ParishLocationQuickSearchResult> = {
        results: [],
        searchValue: '',
    };

    const initialNameResults: ApiSmallResponse<ParishNameQuickSearchResult> = {
        results: [],
        searchValue: '',
    };

    const [quickSearchNameResults, setQuickSearchNameResults] = useState(initialNameResults);
    const [quickSearchNameVisible, setQuickSearchNameVisible] = useState(false);
    const [quickSearchNameInProgress, setQuickSearchNameInProgress] = useState(false);
    const [quickSearchLocationResults, setQuickSearchLocationResults] = useState(initialLocationResults);
    const [quickSearchLocationVisible, setQuickSearchLocationVisible] = useState(false);
    const [quickSearchLocationInProgress, setQuickSearchLocationInProgress] = useState(false);

    const parishTypeOptions = GetParishTypeOptions(parishSettings?.parishTypeMapping);

    const initialValues: ParishQuickSearchFormValues = {
        name: searchFilters?.name,
        cityOrCountry: searchFilters?.location,         
        zip_code: searchFilters?.zip_code,
        type: searchFilters?.type,
    };

    function CloseAutocomplete(flagCallback: () => void) {
        if (!flagCallback) return null;

        setTimeout(() => {
            flagCallback();
        }, 250);
    }

    function QuickSearchNameResult(searchName: string, data) {
        setQuickSearchNameResults({
            ...data,
            searchValue: searchName,
        });

        setQuickSearchNameInProgress(false);
    }

    function QuickSearchCityResult(searchCity: string, data) {
        setQuickSearchLocationResults(
            {
                ...data,
                searchValue: searchCity,
            });
        setQuickSearchLocationInProgress(false);
    }

    function SelectAllResultByName(requestSearchValue: string) {
        if (quickSearchNameResults?.results?.length > 0) {
            performSearch({ name: requestSearchValue });
        }
    }

    function SelectResultByLocation(selectedLocation: ParishLocationQuickSearchResult) {
        if (!selectedLocation) return;

        performSearch({ location: selectedLocation?.location?.trim() });
    }

    function SelectAllResultByLocation(requestSearchValue: string) {
        if (quickSearchLocationResults?.results?.length > 0) {

            performSearch({ location: requestSearchValue });
        }
    }

    function QuickSearchName(searchValue: string) {
        if (searchValue?.length > 1) {
            setQuickSearchNameVisible(true);        
            setQuickSearchNameInProgress(true);
            dispatch(QuickSearchByName(searchValue, (remoteData) => QuickSearchNameResult(searchValue, remoteData)));
        }
    }

    function QuickSearchLocation(searchValue: string) {
        if (searchValue?.length > 1) {
            setQuickSearchLocationVisible(true);
            setQuickSearchLocationInProgress(true);
            dispatch(QuickSearchByLocation(searchValue, (remoteData) => QuickSearchCityResult(searchValue, remoteData)));
        }
    }

    function OnFieldChange(fieldName: string, value: string, formProps: FormikProps<ParishQuickSearchFormValues>) {
        formProps.setFieldTouched(fieldName, true);
        formProps.setFieldValue(fieldName, value);
    }

    function OnNameChanged(e, formProps: FormikProps<ParishQuickSearchFormValues>) {        
        OnFieldChange('name', e?.target?.value, formProps);
        DelayExecute(
            ()=>{ 
                QuickSearchName(e?.target?.value); 
            },
            'name');        
    }

    function OnCityChanged(e, formProps: FormikProps<ParishQuickSearchFormValues>) {        
        OnFieldChange('cityOrCountry', e?.target?.value, formProps);
        DelayExecute(
            ()=>{                 
                QuickSearchLocation(e?.target?.value);
            },
            'cityOrCountry');
    }

    function OnSubmit(submitValues: ParishQuickSearchFormValues) {
        performSearch({
            name: submitValues?.name,
            location: submitValues?.cityOrCountry,
            type: submitValues?.type,
            zip_code: submitValues?.zip_code,
        });
    }

    function OnCancel(canelFormProps: FormikProps<ParishQuickSearchFormValues>) {
        canelFormProps.resetForm({});
        clearForm();
        setQuickSearchNameResults(initialNameResults);
        setQuickSearchLocationResults(initialLocationResults);
        window.scrollTo(0, 0);
    }

    function RenderAutocompleteNoResults(){
        return (<ul className='quickfind-autocomplete-list'>
                        <li className='quickfind-autocomplete-list-item'>
                            <div className='quickfind-autocomplete-link'>
                                <span className='quickfind-autocomplete-link-detail'>{parishSettings?.noResultsAutocompleteMessage || 'Sorry, no results were found.'}</span>
                            </div>
                        </li>
                    </ul>);
    }

    function RenderQuickSearchNameAutocomplete() {
        if (quickSearchNameVisible && !quickSearchNameInProgress) {
            if (quickSearchNameResults?.results?.length > 0){
                return (<ul className='quickfind-autocomplete-list'>
                {quickSearchNameResults.results.map(
                    (item) =>
                        <li className='quickfind-autocomplete-list-item'>
                            <button className='quickfind-autocomplete-link' type='button' onClick={() => selectParishById(item?.party_id)} key={item.party_id}>
                                {item.name}                            
                                {item?.short_address?.trim() && <span className='quickfind-autocomplete-link-detail'>{item?.short_address?.trim()}</span>}
                            </button>
                        </li>,
                )}

                <li className='quickfind-autocomplete-divider'>
                    <button className='quickfind-autocomplete-link' type='button' onClick={() => SelectAllResultByName(quickSearchNameResults?.searchValue)}>{replaceAll(parishSettings?.viewAllResultsTemplate, '{0}', `${quickSearchNameResults?.total_results}`)}</button>
                </li>
                </ul>);
            }

            return (<RenderAutocompleteNoResults />);
        }

        return null;
    }

    function RenderQuickSearchLocationsAutocomplete() {
        if (quickSearchLocationVisible && !quickSearchLocationInProgress) {
            if (quickSearchLocationResults?.results?.length > 0){
                return (<ul className='quickfind-autocomplete-list'>

                {quickSearchLocationResults.results.map(
                    (item) =>
                        <li className='quickfind-autocomplete-list-item'>
                            <button className='quickfind-autocomplete-link' type='button' onClick={() => SelectResultByLocation(item)} key={item?.location?.trim()}>{item?.location?.trim()}</button>
                        </li>,
                )}

                <li className='quickfind-autocomplete-divider'>
                    <button className='quickfind-autocomplete-link' type='button' onClick={() => SelectAllResultByLocation(quickSearchLocationResults?.searchValue)}>{replaceAll(parishSettings?.viewAllResultsTemplate, '{0}', `${quickSearchLocationResults?.total_results}`)}</button>
                </li>
                </ul>);
            }

            return (<RenderAutocompleteNoResults />);
        }

        return null;
    }

    function FormFooter() {
        if (!parishSettings?.termsOfUseMessage) return null;

        return (
            <Wysiwyg content={parishSettings?.termsOfUseMessage} />
        );
    }

    function IsFilterEmpty(formProps: FormikProps<ParishQuickSearchFormValues>): boolean {
        return formProps && formProps.values &&
            !formProps.values.name &&
            !formProps.values.type &&
            !formProps.values.cityOrCountry &&
            !formProps.values.zip_code;
    }

    if (!parishSettingsLoaded || !parishSettings) {
        return <LoadingCircle />;
    }

    return (
        <>
            <h3 className='ecd-page-content-title'>
                {parishSettings.pageTitle}
            </h3>
            <div className='ecd-page-content-section'>
                <p>
                    <strong>{parishSettings.formInstructionMessage}</strong>
                </p>
                <Formik
                    initialValues={initialValues}
                    onSubmit={OnSubmit}
                    validationSchema={QuickSearchFormValidationSchema(parishSettings)}
                    enableReinitialize
                    initialErrors={apiErrors}
                >
                    {(formProps: FormikProps<ParishQuickSearchFormValues>) => (
                        <Form>
                            <FormWrapper
                                formikProps={formProps}
                                submitDisabled={IsFilterEmpty(formProps)}
                                submitText={parishSettings.submitFormButton}
                                cancelText={parishSettings.clearFormButton}
                                onCancel={() => { OnCancel(formProps); }}
                                preventButtonDisableValidation
                                formFooter={FormFooter()}
                            >
                                <div className='form-group'>
                                    <div className='quickfind-parish-section-columns'>                                 
                                        <div className='quickfind-autocomplete-container'>
                                            <FormField
                                                name='name'
                                                label={parishSettings?.nameLabel || 'Enter Parish Name'}
                                                component='input'
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                                onChange={(eventNameChanged) => OnNameChanged(eventNameChanged, formProps)}
                                                onBlur={() => CloseAutocomplete(() => setQuickSearchNameVisible(false))}
                                            />
                                            <RenderQuickSearchNameAutocomplete />
                                        </div>                                        
                                            <FormField
                                                name='type'
                                                label={parishSettings?.parishTypeLabel || 'Parish Type'}
                                                component='input'
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                                options={parishTypeOptions}
                                                select
                                            />                                        
                                    </div>
                                    <div className='quickfind-parish-section-columns'>                                 
                                        <div className='quickfind-autocomplete-container'>
                                            <FormField
                                                name='cityOrCountry'
                                                label={parishSettings?.cityOrCountryLabel || 'Enter City or Country'}
                                                component='input'
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                                onChange={(eventCityChanged) => OnCityChanged(eventCityChanged, formProps)}
                                                onBlur={() => CloseAutocomplete(() => setQuickSearchLocationVisible(false))}
                                            />
                                            <RenderQuickSearchLocationsAutocomplete />
                                        </div>                                        
                                            <FormField
                                                name='zip_code'
                                                label={parishSettings?.zipCodeLabel || 'Zip Code'}
                                                component='input'
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                            />                                        
                                    </div>
                                </div>
                            </FormWrapper>
                        </Form>
                    )}
                </Formik >
            </div>
            <div className='ecd-page-content-section'>
                <Wysiwyg content={parishSettings?.postFormMessage} />
            </div>
        </>
    );
}

export default ParishSearch;
